/*
  Conversion tracking for the Meta (Facebook) pixel.

  The events the site already reports to GA4 carry everything the pixel needs, so instead of
  instrumenting every call site again this maps them onto the Meta standard events as they pass
  through the data layer.

  The pixel itself is loaded by the page layout and Cookiebot holds it back until the visitor
  accepts marketing cookies, so window.fbq does not exist for the first moments of a page and may
  never exist at all. An event reported before it arrives is held and sent once it is there.
*/

const standardEvents = {
  view_item: 'ViewContent',
  add_to_cart: 'AddToCart',
  begin_checkout: 'InitiateCheckout',
  add_payment_info: 'AddPaymentInfo',
  purchase: 'Purchase',
  sign_up: 'CompleteRegistration',
  Search: 'Search'
}

/*
  Events reported before the pixel was there, oldest first.

  The order receipt is what this is for. It reads the order over HTTP and reports the purchase in
  the callback, which on a quick reply lands before Cookiebot has turned the pixel tag from
  text/plain into a running script -- so the one event the whole thing exists to report was the one
  most likely to be dropped. GTM never had the problem: a push onto the data layer is an append to
  an array, which GTM reads whenever it happens to load.
*/
const pendingCalls = []

// Enough for any one page. A visitor who somehow reports more than this without the pixel ever
// arriving has not consented, and the events are not wanted.
const maxPendingCalls = 50

// How long to keep looking for the pixel before giving the events up.
const pixelWaitMs = 30000
const pixelPollMs = 250

let waitingForPixel = false

function send([name, parameters, options]) {
  if (options) {
    window.fbq('track', name, parameters, options)
  } else if (parameters) {
    window.fbq('track', name, parameters)
  } else {
    window.fbq('track', name)
  }
}

function sendPending() {
  if (!window.fbq) {
    return false
  }

  while (pendingCalls.length) {
    send(pendingCalls.shift())
  }

  return true
}

function waitForPixel() {
  if (waitingForPixel) {
    return
  }

  waitingForPixel = true

  // Cookiebot says when it has been given consent, which is when it rewrites the pixel tag.
  window.addEventListener('CookiebotOnAccept', sendPending)
  window.addEventListener('CookiebotOnConsentReady', sendPending)

  // Those have been and gone for a visitor who consented on an earlier page, so the pixel is
  // watched for as well rather than relied on being announced.
  const startedAt = Date.now()
  const poll = setInterval(() => {
    if (sendPending() || Date.now() - startedAt > pixelWaitMs) {
      clearInterval(poll)
    }
  }, pixelPollMs)
}

function dispatch(call) {
  if (window.fbq) {
    // Anything still held goes first, so the events keep the order they happened in.
    sendPending()
    send(call)

    return
  }

  if (pendingCalls.length < maxPendingCalls) {
    pendingCalls.push(call)
  }

  waitForPixel()
}

function createContents(items) {
  return items.map((item) => ({
    id: item.item_id,
    quantity: item.quantity,
    item_price: item.price
  }))
}

function createParameters(event, ecommerce) {
  const items = ecommerce.items || []
  const parameters = {
    currency: ecommerce.currency,
    value: ecommerce.value
  }

  if (items.length) {
    parameters.content_type = 'product'
    parameters.content_ids = items.map((item) => item.item_id)
    parameters.contents = createContents(items)
  }

  if (event === 'purchase') {
    parameters.num_items = items.reduce((total, item) => total + item.quantity, 0)
  }

  return parameters
}

function trackEvent(eventObject) {
  const standardEvent = standardEvents[eventObject.event]
  if (!standardEvent) {
    return
  }

  // The search event is reported in the generic shape, where the query is the label.
  if (eventObject.event === 'Search') {
    dispatch([standardEvent, { search_string: eventObject.label }])

    return
  }

  const ecommerce = eventObject.ecommerce
  if (!ecommerce) {
    dispatch([standardEvent])

    return
  }

  // The order number lets Meta discard the duplicate if the purchase is ever reported through
  // the Conversions API as well.
  const options = ecommerce.transaction_id ? { eventID: ecommerce.transaction_id } : undefined

  dispatch([standardEvent, createParameters(eventObject.event, ecommerce), options])
}

export default {
  trackEvent
}
